<template>
  <v-app :dark="theme.isDark">
    <v-layout v-if="!currentUserLoaded" row justify-center align-center>
      <spinner />
    </v-layout>

    <template v-else-if="isUserLoggedIn">
      <div class="app-layout">
        <app-sidebar v-if="!inIframe && !hideSidebar" />
        <router-view />
      </div>

      <chat-agent />
      <app-modals />
    </template>
  </v-app>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import theme from '@/factories/theme';
import { inIframe } from '@/globals';
import intercom from '@/lib/intercom';
import { initAppAnalytics } from '@/lib/analytics';
import api from '@/factories/api';
import { update as updateIntercom } from '@intercom/messenger-js-sdk';

import AppSidebar from '@/components/layouts/sidebar/AppSidebar';
import AppModals from '@/components/layouts/AppModals';
import ChatAgent from '@/components/basic/chat-agent/ChatAgent.vue';

export default {
  name: 'AppLayout',
  components: {
    AppSidebar,
    AppModals,
    ChatAgent,
  },
  data() {
    return {
      theme,
      inIframe,
      jwtRefreshing: false,
    };
  },
  computed: {
    ...mapState('users', ['currentUser', 'currentUserLoaded']),
    ...mapGetters('users', ['isUserLoggedIn']),
    hideSidebar() {
      return this.$route.meta?.hideSidebar;
    },
  },
  methods: {
    ...mapActions('users', ['loadCurrentUser', 'loadAchievements']),
    ...mapActions('programs', ['loadPrograms']),
    ...mapActions('org', ['loadOrgSettings', 'loadUsageLimits']),
    redirectToLogin() {
      if (this.isUserLoggedIn) return;

      const redirect = this.$route.fullPath;
      const query = redirect === '/' ? {} : { redirect };
      this.$router.push({ name: 'app-login', query });
    },

    checkAchievements() {
      if (!this.currentUser?.owner) return Promise.resolve();
      return this.loadAchievements();
    },

    checkHomePage() {
      if (!this.isVectorRecruiting) return;

      if (this.$route.name === 'home') {
        this.$router.push({ name: 'recruiting' });
      }
    },

    async initializeAuthenticatedApp() {
      this.checkHomePage();

      await Promise.allSettled([
        this.loadUsageLimits(),
        this.checkAchievements(),
        this.loadPrograms(),
        this.loadOrgSettings(),
      ]);
    },

    async checkCurrentUser() {
      try {
        await this.loadCurrentUser();

        if (!this.isUserLoggedIn) {
          this.redirectToLogin();
          return;
        }

        await this.initializeAuthenticatedApp();
      } catch (error) {
        this.redirectToLogin();
      }
    },

    shouldRefreshIntercomJwt() {
      return (
        this.isUserLoggedIn &&
        !inIframe &&
        document.visibilityState === 'visible' &&
        !this.jwtRefreshing
      );
    },

    async refreshIntercomJwtOnFocus() {
      if (!this.shouldRefreshIntercomJwt()) return;

      this.jwtRefreshing = true;
      try {
        const userInfo = await api().get('users/current');
        const intercomJwt = userInfo?.intercom_user_jwt;
        if (intercomJwt) {
          updateIntercom({ intercom_user_jwt: intercomJwt });
        }
      } finally {
        this.jwtRefreshing = false;
      }
    },
  },
  created() {
    initAppAnalytics();
    this.checkCurrentUser();
  },
  mounted() {
    intercom.init();
    document.addEventListener(
      'visibilitychange',
      this.refreshIntercomJwtOnFocus
    );
  },
  beforeDestroy() {
    document.removeEventListener(
      'visibilitychange',
      this.refreshIntercomJwtOnFocus
    );
  },
};
</script>
